<template>
  <div>
    <div v-if="loading" class="flex justify-center items-center min-h-[200px]">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-yellow"></div>
    </div>
    <template v-else>
    <div class="relative overflow-x-auto shadow-md sm:rounded-lg mt-5">
      <table class="w-full text-left rtl:text-right text-gray-700">
        <thead>
          <tr class="bg-gray-100">
            <th scope="col" class="p-4">
              <div class="flex items-center">
                <input id="checkbox-all-search" type="checkbox"
                  class="w-4 h-4 text-custom-yellow bg-gray-100 border-gray-300 rounded focus:ring-custom-yellow dark:focus:ring-custom-yellow dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600" />
                <label for="checkbox-all-search" class="sr-only">checkbox</label>
              </div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">
                Document Name
              </div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">
                Document Type
              </div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">
                Department
              </div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">
                Audience No.
              </div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">
                Deadline
              </div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">
                Date Uploaded
              </div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">
                Status
              </div>
            </th>
            <th scope="col" class="px-6">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(document, index) in filteredDocuments" :key="index" class="border-b">
            <td class="w-4 p-4">
              <div class="flex items-center">
                <input :id="'checkbox-table-search-' + index" type="checkbox" class="w-4 h-4" />
                <label :for="'checkbox-table-search-' + index" class="sr-only">checkbox</label>
              </div>
            </td>
            <td class="px-6 py-1">{{ document.document_name }}</td>
            <td class="px-6 py-1">{{ document.document_type }}</td>
            <td class="px-6 py-1">
              <ul>
                <li v-for="(department, index) in document.departments" :key="department.department_id">
                  {{ department.name }}
                  <span v-if="index < document.departments.length - 1">,</span>
                </li>
              </ul>
            </td>
            <td class="px-6 py-1 text-center">
              {{ document.audience_number }}
            </td>
            <td class="px-6 py-1">{{ new Date(document.deadline).toISOString().split('T')[0] }}</td>
            <td class="px-6 py-1">{{ new Date(document.date_uploaded).toISOString().split('T')[0] }}</td>
            <td class="px-6 py-2">
              <span class="p-2 rounded-lg" :class="{
                'bg-green-200 text-green-800': ['Active'].includes(document.status),
                'bg-red-200 text-red-800': ['Inactive'].includes(document.status),
                'bg-yellow-200 text-yellow-800': ['Archived'].includes(document.status)
              }">
                {{ document.status }}
              </span>
            </td>
            <td class="px-6 py-2 text-center">
              <button class="inline-flex items-center gap-2 text-custom-blue hover:text-custom-blue/80"
                @click="openReadersModal(document)">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
                  <path d="M12 5c-7.633 0-10 7-10 7s2.367 7 10 7 10-7 10-7-2.367-7-10-7Zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10Zm0-2a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"/>
                </svg>
                <span class="text-sm">Readers</span>
              </button>
            </td>
            <td class="flex items-center px-4 py-2">
              <CustomButton label="Details" bg-class="bg-custom-yellow text-sm py-1" @click="
                handleShowDetails(document.document_id)" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- Readers Modal -->
    <div v-if="showReaders" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black/40" @click="closeReadersModal"></div>
      <div class="relative bg-white w-full max-w-3xl mx-4 rounded-lg shadow-lg">
        <div class="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 class="text-lg font-semibold text-gray-800">Readers</h3>
            <p class="text-sm text-gray-500" v-if="activeDocument">
              {{ activeDocument.document_name }}
            </p>
          </div>
          <button class="text-gray-500 hover:text-gray-700" @click="closeReadersModal">✕</button>
        </div>
        <div class="px-6 py-4">
          <div v-if="readersLoading" class="py-8 text-center text-gray-500">Loading readers…</div>
          <div v-else>
            <div v-if="readersError" class="mb-3 text-red-600">{{ readersError }}</div>
            <div v-if="readers.length === 0" class="py-8 text-center text-gray-500">No readers yet.</div>
            <div v-else class="relative overflow-x-auto">
              <table class="w-full text-left text-gray-700">
                <thead>
                  <tr class="bg-gray-100">
                    <th class="px-4 py-2">Name</th>
                    <th class="px-4 py-2">Email</th>
                    <th class="px-4 py-2">Read at</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, i) in readers" :key="i" class="border-b">
                    <td class="px-4 py-2">{{ r.first_name }} {{ r.last_name }}</td>
                    <td class="px-4 py-2">{{ r.email }}</td>
                    <td class="px-4 py-2">
                      <span class="text-gray-800">{{ formatDateTime(r.read_at) }}</span>
                      <span class="text-gray-400"> · {{ relativeTime(r.read_at) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Readers Pagination -->
            <div class="flex justify-between items-center mt-4" v-if="readersTotalPages > 1">
              <button @click="readersPrevPage" :disabled="readersPage === 1"
                class="px-4 py-2 text-custom-blue disabled:opacity-50">Previous</button>
              <div class="text-sm text-gray-600">Page {{ readersPage }} of {{ readersTotalPages }}</div>
              <button @click="readersNextPage" :disabled="readersPage === readersTotalPages"
                class="px-4 py-2 text-custom-blue disabled:opacity-50">Next</button>
            </div>
          </div>
        </div>
        <div class="px-6 py-4 border-t flex justify-end">
          <button class="bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded" @click="closeReadersModal">Close</button>
        </div>
      </div>
    </div>
    <!-- Pagination -->
    <div class="flex justify-center gap-4 mt-4">
      <button @click="previousPage" :disabled="currentPage === 1"
        class="flex items-center gap-2 px-6 py-3 font-bold text-center text-custom-blue align-middle transition-all rounded-lg select-none hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
        type="button">
        <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
          viewBox="0 0 24 24">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="m15 19-7-7 7-7" />
        </svg>
        Previous
      </button>

      <div class="flex items-center gap-2">
        <button v-for="page in totalPages" :key="page" @click="goToPage(page)" :class="{
          'bg-custom-blue text-white': page === currentPage,
          'text-custom-blue': page !== currentPage
        }"
          class="relative h-10 max-h-[40px] w-10 max-w-[40px] select-none rounded-lg text-center align-middle font-medium transition-all hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button">
          <span class="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">
            {{ page }}
          </span>
        </button>
      </div>

      <button @click="nextPage" :disabled="currentPage === totalPages"
        class="flex items-center gap-2 px-6 py-3 font-bold text-center text-custom-blue align-middle transition-all rounded-lg select-none hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
        type="button">
        Next
        <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
          viewBox="0 0 24 24">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="m9 5 7 7-7 7" />
        </svg>
      </button>
    </div>
  </template>
  </div>
</template>

<script>
import CustomButton from "@/components/CustomButton.vue";
import api from "@/services/api";

export default {
  name: "SupervisorTrainingMaterialTab",
  components: {
    CustomButton,
  },
  created() {
    this.fetchDocuments();
  },
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      documents: [],
      error: null,
      filters: {
        documentType: "",
        department_id: "",
        deadlineDate: "",
        dateUploaded: "",
        documentStatus: "",
      },
      currentPage: 1,
      totalPages: 0,
      perPage: 10,
      // readers modal state
      showReaders: false,
      activeDocument: null,
      readers: [],
      readersLoading: false,
      readersError: null,
      readersPage: 1,
      readersTotalPages: 1,
      readersPerPage: 10,
     
    };
  },
  computed: {
    filteredDocuments() {
      return this.documents.filter((document) => {
        const search = this.searchQuery.toLowerCase();
        const safeToLower = (value) =>
          typeof value === "string" ? value.toLowerCase() : "";

        // Apply search filter
        const matchesSearch =
          safeToLower(document.document_name).includes(search) ||
          safeToLower(document.document_type).includes(search) ||
          safeToLower(document.status).includes(search) ||
          safeToLower(new Date(document.date_uploaded).toLocaleDateString("en-CA")).includes(search);


        // Apply additional filters
        const matchesDocumentType =
          this.filters.documentType === "" ||
          safeToLower(document.document_type) === safeToLower(this.filters.documentType);

        const matchesDepartment =
          this.filters.department_id === "" ||
          safeToLower(document.departments?.name) === safeToLower(this.filters.department_id);
          

          const matchesDeadline =
          this.filters.deadlineDate === "" ||
          new Date(document.deadline)
            .toLocaleDateString("en-CA")
            .includes(this.filters.deadlineDate);

        const matchesDateRegistered =
          this.filters.dateUploaded === "" ||
          new Date(document.date_uploaded)
            .toLocaleDateString("en-CA")
            .includes(this.filters.dateUploaded);

        const matchesStatus =
          this.filters.documentStatus === "" ||
          safeToLower(document.status) === safeToLower(this.filters.documentStatus);

        return matchesSearch && matchesDepartment && matchesDocumentType && matchesDeadline && matchesDateRegistered && matchesStatus;
      });
    },
  },
  watch: {
    filters: { deep: true, handler: 'fetchDocuments' },
    searchQuery: 'fetchDocuments'
  },
  methods: {
    async fetchDocuments() {
      this.loading = true;
      try {
        const queryParams = { ...this.filters, search_query: this.searchQuery, page: this.currentPage, per_page: this.perPage };
        const response = await api.getDocuments(queryParams);
        this.documents = response.data || response;
        this.currentPage = response.current_page;
        this.totalPages = response.total_pages;
      } catch (error) {
        this.error = error.message || "Failed to fetch documents";
      } finally {
        this.loading = false;
      }
    },

    applyFilters(filters) {
      this.filters = { ...this.filters, ...filters };
      this.currentPage = 1;
      this.fetchDocuments();
    },

    resetFilters() {
      this.filters = {
        documentType: "",
        department_id: "",
        deadlineDate: "",
        dateUploaded: "",
        documentStatus: "",
      };
      this.currentPage = 1;
      this.fetchDocuments();
    },

    async goToPage(page) {
      this.currentPage = page;
      await this.fetchDocuments();
    },

    async previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        await this.fetchDocuments();
      }
    },

    async nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        await this.fetchDocuments();
      }
    },


    handleShowDetails(document_id) {
      this.$router.push({
        name: 'SupervisorDocumentsDetailsPage',
        params: { document_id }
      });
    },

    openReadersModal(document) {
      this.activeDocument = document;
      this.showReaders = true;
      this.readersPage = 1;
      this.fetchReaders();
    },
    closeReadersModal() {
      this.showReaders = false;
      this.activeDocument = null;
      this.readers = [];
      this.readersError = null;
    },
    async fetchReaders() {
      if (!this.activeDocument?.document_id) return;
      this.readersLoading = true;
      this.readersError = null;
      try {
        const res = await api.getDocumentReadersPaged(this.activeDocument.document_id, this.readersPage, this.readersPerPage);
        this.readers = res?.data || [];
        this.readersTotalPages = res?.total_pages || 1;
      } catch (e) {
        this.readersError = e.message || 'Failed to load readers';
        this.readers = [];
        this.readersTotalPages = 1;
      } finally {
        this.readersLoading = false;
      }
    },
    async readersPrevPage() {
      if (this.readersPage > 1) {
        this.readersPage -= 1;
        await this.fetchReaders();
      }
    },
    async readersNextPage() {
      if (this.readersPage < this.readersTotalPages) {
        this.readersPage += 1;
        await this.fetchReaders();
      }
    },
    formatDateTime(value) {
      try { return new Date(value).toLocaleString(); } catch { return value; }
    },
    relativeTime(value) {
      try {
        const now = new Date();
        const then = new Date(value);
        const diffMs = now - then;
        const sec = Math.floor(diffMs / 1000);
        const min = Math.floor(sec / 60);
        const hr = Math.floor(min / 60);
        const day = Math.floor(hr / 24);
        const month = Math.floor(day / 30);
        const year = Math.floor(day / 365);
        if (sec < 5) return 'just now';
        if (sec < 60) return `${sec} seconds ago`;
        if (min < 2) return 'a minute ago';
        if (min < 60) return `${min} minutes ago`;
        if (hr < 2) return 'an hour ago';
        if (hr < 24) return `${hr} hours ago`;
        if (day < 2) return 'yesterday';
        if (day < 30) return `${day} days ago`;
        if (month < 2) return 'a month ago';
        if (month < 12) return `${month} months ago`;
        if (year < 2) return 'a year ago';
        return `${year} years ago`;
      } catch { return ''; }
    },
  },
};
</script>

<style lang="css" scoped></style>
